import { cellStyle, imagePlaceholer } from 'appConstants';
import { getSocialImageUrl } from '../Social/utils';

// Email-mode "rows": pre-composed, edit-in-place content blocks (see DND_EMAIL_MODE_SPEC.md).
//
// Each row is a LOCKED table scaffold (data-gjs-*="false": not selectable/editable/droppable)
// so a basic user can't break the layout or accidentally nest things. The CONTENT inside a cell
// (text/image/button/link) stays selectable + editable, so the user edits in place (double-click
// text, click image to replace) rather than dragging pieces into cells. Rows are added via the
// block picker / click-to-add and reordered via the row toolbar (later slices); they are not
// drop targets themselves.
//
// This module is loaded ONLY in Email mode; the standard Basic/Extra/Social palettes are skipped
// there. Component TYPES (text/image/button/link) are still registered by the normal
// components() calls, so the markup below is recognised correctly.

// Small inline icons (self-contained; no icon-font dependency).
const ICON = {
  text: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M18.5,4L19.66,8.35L18.7,8.61C18.25,7.74 17.79,6.87 17.26,6.43C16.73,6 16.11,6 15.5,6H13V16.5C13,17 13,17.5 13.33,17.75C13.67,18 14.33,18 15,18V19H9V18C9.67,18 10.33,18 10.67,17.75C11,17.5 11,17 11,16.5V6H8.5C7.89,6 7.27,6 6.74,6.43C6.21,6.87 5.75,7.74 5.3,8.61L4.34,8.35L5.5,4H18.5Z"/></svg>',
  heading:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M20,20H4A2,2 0 0,1 2,18V6A2,2 0 0,1 4,4H20A2,2 0 0,1 22,6V18A2,2 0 0,1 20,20M4,6V18H20V6H4M6,9H18V11H6V9M6,13H16V15H6V13Z"/></svg>',
  image:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M21,3H3C2,3 1,4 1,5V19A2,2 0 0,0 3,21H21C22,21 23,20 23,19V5C23,4 22,3 21,3M5,17L8.5,12.5L11,15.5L14.5,11L19,17H5Z"/></svg>',
  button:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M20 20.5C20 21.3 19.3 22 18.5 22H13C12.6 22 12.3 21.9 12 21.6L8 17.4L8.7 16.6C8.9 16.4 9.2 16.3 9.5 16.3H9.7L12 18V9C12 8.4 12.4 8 13 8S14 8.4 14 9V13.5L15.2 13.6L19.1 15.8C19.6 16 20 16.6 20 17.1V20.5M20 2H4C2.9 2 2 2.9 2 4V12C2 13.1 2.9 14 4 14H8V12H4V4H20V12H18V14H20C21.1 14 22 13.1 22 12V4C22 2.9 21.1 2 20 2Z"/></svg>',
  imageText:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M3 5v14h18V5H3m2 2h6v6H5V7m8 0h6v2h-6V7m0 4h6v2h-6v-2m-8 4h14v2H5v-2Z"/></svg>',
  divider:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M21 18H2V20H21V18M19 10V14H4V10H19M20 8H3C2.45 8 2 8.45 2 9V15C2 15.55 2.45 16 3 16H20C20.55 16 21 15.55 21 15V9C21 8.45 20.55 8 20 8M21 4H2V6H21V4Z"/></svg>',
  spacer:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M4 5h16v2H4V5m0 12h16v2H4v-2m8-9 4 4h-3v2h-2v-2H8l4-4Z"/></svg>',
  footer:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M20 6H4V4h16v2m0 4H4V8h16v2M4 14v-2h16v2H4m0 4v-2h10v2H4Z"/></svg>',
  header:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M20 4H4a2 2 0 0 0-2 2v3h20V6a2 2 0 0 0-2-2m0 7H4v9h16v-9M6 6.5h5v1H6v-1m9.5 0h2.5v1h-2.5v-1Z"/></svg>',
  columns:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M3 5v14h8V5H3m10 0v14h8V5h-8m-2 12H5V7h6v10m8 0h-6V7h6v10Z"/></svg>',
  social:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81a3 3 0 1 0-3-3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9a3 3 0 1 0 0 6c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65a2.92 2.92 0 1 0 2.92-2.92Z"/></svg>',
  unsubscribe:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h9.1a6 6 0 0 1-.1-1 6 6 0 0 1 .35-2H4V8l8 5 8-5v3.1a6 6 0 0 1 2 1.2V6c0-1.1-.9-2-2-2m-8 7L4 6h16l-8 5m3 8v-2h6v2h-6Z"/></svg>',
};

// Header nav / small link text.
const NAV =
  'data-gjs-draggable="false" style="font-family: Arial, sans-serif; font-size: 13px; line-height: 1.5; color: #555;"';

// LOCKED scaffold attributes for the structural table/tr/td of a row.
const LOCK =
  'data-gjs-selectable="false" data-gjs-hoverable="false" data-gjs-editable="false" data-gjs-droppable="false"';

// Email-safe row table style: full-width, collapsed borders, and — crucially — NO fixed height.
// (The shared `tableStyle` bakes in height:150px for Advanced-mode empty drag placeholders; using
// it here made every pre-filled email row ~150px tall with dead space. See DND_EMAIL_MODE audit.)
const ROW_STYLE = 'width:100%;border-collapse:collapse;';

// A full-width row wrapping one or more cells. The outer <table> is an `email-row` component
// (selectable → shows the row hover-toolbar: move up/down, duplicate, delete — see ./components.js);
// the inner tbody/tr/td are LOCKED so the scaffold can't be broken. `cells` is the inner <td> markup.
// role="presentation" + border/cellpadding/cellspacing keep screen readers from announcing it as a
// data table and stop clients adding default table spacing.
const row = (cells) => `
  <table data-gjs-type="email-row" data-type="section" role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="${ROW_STYLE}">
    <tbody ${LOCK}>
      <tr ${LOCK}>${cells}</tr>
    </tbody>
  </table>`;

const cell = (content, width = '100%', extra = '', cls = '') =>
  `<td ${LOCK} class="${cls}" style="${cellStyle} width: ${width}; ${extra}">${content}</td>`;

// Mobile-stacking rule for multi-column rows: side-by-side cells become full-width blocks on small
// screens (email-safe media query; kept in a <style>, so it survives server-side CSS inlining). See
// DND_EMAIL_MODE_SPEC.md §7 (slice 5b).
const STACK_ON_MOBILE =
  '<style>@media (max-width: 480px){ .email-col{ display:block !important; width:100% !important; } }</style>';

// LOCK_DRAG: content inside a row is edit-in-place but NOT draggable, so a beginner can only ever
// move whole ROWS (never a stray paragraph/image). Matches how the existing builder locks inner
// content (see DND_EMAIL_MODE_DND_PLACEMENT_REVIEW_2026-07-21.md).
const LOCK_DRAG = 'data-gjs-draggable="false"';

// A non-draggable copy of the shared image placeholder (locked for email rows only).
const lockedImage = imagePlaceholer.replace('<img ', `<img ${LOCK_DRAG} `);

// Default styles for content pieces (inline so they render before server-side CSS inlining).
const TEXT = `${LOCK_DRAG} style="font-family: Arial, sans-serif; font-size: 15px; line-height: 1.5; color: #333;"`;
const H1 = `${LOCK_DRAG} style="font-family: Arial, sans-serif; font-size: 24px; line-height: 1.3; color: #111; margin: 0 0 8px;"`;

// Outlook-bulletproof CTA. Outlook (Word engine) ignores padding/background/border-radius on an
// inline <a>, collapsing styled buttons to a plain link. So the padding + background live on a
// <td bgcolor> (which Outlook honours) and the <a> carries only the label + text color. The wrapper
// table/td are LOCKED, so a beginner edits only the button text; the <a> stays the `button` component
// (its href is editable). See DND_EMAIL_MODE audit (MSO button gap).
const button = (
  label = 'Shop now',
  href = '#',
  bg = '#d983a6',
  color = '#ffffff',
) => `
  <table role="presentation" border="0" cellpadding="0" cellspacing="0" ${LOCK} style="margin:0 auto;border-collapse:collapse;">
    <tbody ${LOCK}><tr ${LOCK}>
      <td ${LOCK} align="center" bgcolor="${bg}" style="border-radius:5px;padding:14px 28px;text-align:center;">
        <a ${LOCK_DRAG} data-gjs-type="button" data-type="button" class="bpreview-button" href="${href}" style="display:inline-block;color:${color};text-decoration:none;font-family:Arial, sans-serif;font-size:16px;line-height:1;">${label}</a>
      </td>
    </tr></tbody>
  </table>`;

// Shared row catalogue — the single source of truth for the Email row set. Used BOTH to register
// the BlockManager blocks (drag-to-add, below) AND to power the click-to-add "+" picker
// (see ./components.js, slice 3b). Each entry is static (content is a plain HTML string).
export const buildEmailRows = ({ baseUrl } = {}) => [
  {
    id: 'email-header',
    label: 'Header',
    media: ICON.header,
    content:
      row(
        cell(lockedImage, '50%', 'vertical-align: middle;', 'email-col') +
          cell(
            `<p ${NAV}>Home&nbsp;&nbsp;|&nbsp;&nbsp;Shop&nbsp;&nbsp;|&nbsp;&nbsp;About</p>`,
            '50%',
            'vertical-align: middle; text-align: right;',
            'email-col',
          ),
      ) + STACK_ON_MOBILE,
  },
  {
    id: 'email-text',
    label: 'Text',
    media: ICON.text,
    content: row(
      cell(`<p ${TEXT}>Write your text here. Double-click to edit.</p>`),
    ),
  },
  {
    id: 'email-heading',
    label: 'Heading',
    media: ICON.heading,
    content: row(
      cell(
        `<h1 ${H1}>Your heading</h1><p ${TEXT}>Supporting text goes here.</p>`,
      ),
    ),
  },
  {
    id: 'email-image',
    label: 'Image',
    media: ICON.image,
    content: row(cell(lockedImage)),
  },
  {
    id: 'email-image-text',
    label: 'Image + Text',
    media: ICON.imageText,
    content:
      row(
        cell(lockedImage, '40%', 'vertical-align: top;', 'email-col') +
          cell(
            `<h1 ${H1}>Title</h1><p ${TEXT}>Describe your product or update here.</p>`,
            '60%',
            'vertical-align: top;',
            'email-col',
          ),
      ) + STACK_ON_MOBILE,
  },
  {
    id: 'email-columns',
    label: '2 Columns',
    media: ICON.columns,
    content:
      row(
        cell(
          `<p ${TEXT}>Column one — double-click to edit.</p>`,
          '50%',
          'vertical-align: top;',
          'email-col',
        ) +
          cell(
            `<p ${TEXT}>Column two — double-click to edit.</p>`,
            '50%',
            'vertical-align: top;',
            'email-col',
          ),
      ) + STACK_ON_MOBILE,
  },
  {
    id: 'email-button',
    label: 'Button',
    media: ICON.button,
    content: row(cell(button(), '100%', 'text-align: center;')),
  },
  {
    id: 'email-divider',
    label: 'Divider',
    media: ICON.divider,
    content: row(
      cell(
        '<div data-gjs-type="divider" data-gjs-draggable="false" style="width: 100%; margin: 8px 0; background-color: rgba(0,0,0,0.1); height: 1px;"></div>',
      ),
    ),
  },
  {
    id: 'email-spacer',
    label: 'Spacer',
    media: ICON.spacer,
    content: row(
      cell(
        '<div data-gjs-type="divider" data-gjs-draggable="false" style="width: 100%; height: 24px; line-height: 24px; font-size: 0;">&nbsp;</div>',
      ),
    ),
  },
  {
    id: 'email-social',
    label: 'Social',
    media: ICON.social,
    content: row(
      cell(
        `<div ${LOCK_DRAG} style="text-align: center;">` +
          ['facebook', 'twitter', 'instagram', 'linkedin']
            .map(
              (network) =>
                `<a href="#" ${LOCK_DRAG} style="display: inline-block; margin: 0 6px;"><img ${LOCK_DRAG} src="${getSocialImageUrl(
                  { baseUrl, network },
                )}" alt="${network}" width="32" height="32" style="display: inline-block; border: 0;"/></a>`,
            )
            .join('') +
          `</div>`,
        '100%',
        'text-align: center;',
      ),
    ),
  },
  {
    // Standalone Unsubscribe link. Some layouts want the unsubscribe line on its own (e.g. above a
    // full footer, or as the only compliance line) rather than baked into the Footer block. Same
    // `{$unsubscribeLink}` merge tag, resolved per-recipient downstream — so it always yields a
    // working, per-recipient unsubscribe URL (deliverability: see DND_BUILDER_UNSUBSCRIBE_AND_TOKENS.md).
    id: 'email-unsubscribe',
    label: 'Unsubscribe',
    media: ICON.unsubscribe,
    content: row(
      cell(
        `<p ${LOCK_DRAG} style="font-family: Arial, sans-serif; font-size: 12px; line-height: 1.5; color: #888888; text-align: center;">` +
          `Prefer not to receive these emails? ` +
          `<a href="{$unsubscribeLink}" style="color: #888888; text-decoration: underline;">Unsubscribe</a>` +
          `</p>`,
        '100%',
        'text-align: center;',
      ),
    ),
  },
  {
    // Footer ships an unsubscribe link by construction (deliverability: see
    // DND_BUILDER_UNSUBSCRIBE_AND_TOKENS.md). {$unsubscribeLink} is resolved per-recipient downstream.
    id: 'email-footer',
    label: 'Footer',
    media: ICON.footer,
    content: row(
      cell(
        `<p ${LOCK_DRAG} style="font-family: Arial, sans-serif; font-size: 12px; line-height: 1.5; color: #888888; text-align: center;">` +
          `You received this email because you subscribed to our list.<br/>` +
          `<a href="{$unsubscribeLink}" style="color: #888888; text-decoration: underline;">Unsubscribe</a>` +
          `</p>`,
      ),
    ),
  },
];

export default function (editor, { category, baseUrl }) {
  const bm = editor.BlockManager;
  buildEmailRows({ baseUrl }).forEach(({ id, label, media, content }) =>
    bm.add(id, {
      label,
      category,
      media,
      select: true,
      activate: true,
      content,
    }),
  );
}
